import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const backgrounds = [
  {
    gradient: 'from-pink-900/30 via-purple-900/20 to-cyan-900/30',
    accent: '#FF69B4',
    label: 'Elegance'
  },
  {
    gradient: 'from-amber-900/30 via-orange-900/20 to-red-900/30',
    accent: '#FFD700',
    label: 'Power'
  },
  {
    gradient: 'from-cyan-900/30 via-blue-900/20 to-purple-900/30',
    accent: '#06b6d4',
    label: 'Grace'
  }
];

export default function Home() {
  const [currentBg, setCurrentBg] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    const interval = setInterval(() => {
      setCurrentBg((prev) => (prev + 1) % backgrounds.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: '🏆',
      title: 'Championship Training',
      description: 'World-class coaching from elite professionals who have competed at the highest levels.'
    },
    {
      icon: '✨',
      title: 'VIP Experience',
      description: 'Premium facilities with state-of-the-art equipment and personalized attention.'
    },
    {
      icon: '🎯',
      title: 'All Ages Welcome',
      description: 'From tiny tumblers to competitive athletes, everyone belongs here.'
    },
    {
      icon: '🌟',
      title: 'Fun & Fitness',
      description: 'Where passion meets play. Build strength, flexibility, and lifelong friendships.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Parent',
      content: 'My daughter has transformed from shy to confident. The coaches are incredible!'
    },
    {
      name: 'James Thompson',
      role: 'Competition Coach',
      content: 'The facilities rival anything I\'ve seen in the UK. Truly world-class.'
    },
    {
      name: 'Emma Rodriguez',
      role: 'Member since 2020',
      content: 'Best decision we ever made. Our whole family loves Elite Gymnastics!'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Animated Hero Section */}
      <section className="hero-bg min-h-screen flex items-center justify-center relative px-4 pt-24">
        {/* Floating Particles */}
        <div className="hero-particles">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="particle" />
          ))}
        </div>

        {/* Gradient Orbs */}
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 transition-all duration-1000"
          style={{
            background: `radial-gradient(circle, ${backgrounds[currentBg].accent}40 0%, transparent 70%)`,
            transform: 'translate(-50%, -50%)'
          }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 transition-all duration-1000"
          style={{
            background: `radial-gradient(circle, ${backgrounds[(currentBg + 1) % backgrounds.length].accent}40 0%, transparent 70%)`,
            transform: 'translate(50%, 50%)'
          }}
        />

        {/* Hero Content */}
        <div className={`text-center z-10 max-w-5xl mx-auto transition-all duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
          {/* Badge */}
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full glass mb-12 animate-fade-in">
            <span className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
            <span className="text-base font-medium text-gray-300">Classes Running Now - Join Any Time!</span>
          </div>

          {/* Main Title */}
          <h1 className="hero-title mb-6 text-balance">
            Elite Gymnastics
          </h1>

          {/* Subtitle */}
          <p className="hero-subtitle mb-4 max-w-2xl mx-auto">
            Where Champions Are Made
          </p>
          
          <p className="text-lg text-gray-400 mb-12 max-w-xl mx-auto">
            London's premier gymnastics club, inspiring excellence since 2010
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <Link to="/membership" className="btn-vip">
              Start Your Journey
            </Link>
            <Link to="/classes" className="btn-ghost-vip">
              View Classes
            </Link>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto animate-slide-up" style={{ animationDelay: '0.6s' }}>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-gradient-gold">500+</div>
              <div className="text-sm text-gray-500 mt-1">Active Members</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-gradient-pink">15+</div>
              <div className="text-sm text-gray-500 mt-1">Elite Coaches</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-cyan-400">12</div>
              <div className="text-sm text-gray-500 mt-1">Years Strong</div>
            </div>
          </div>

          {/* Background Indicator */}
          <div className="flex justify-center gap-2 mt-12">
            {backgrounds.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentBg(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  currentBg === i ? 'w-8 bg-gradient-to-r from-pink-500 to-amber-400' : 'bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2">
            <div className="w-1 h-2 rounded-full bg-white/50 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-900/5 to-transparent" />
        
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-16">
            <span className="badge-vip mb-4">Why Choose Us</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gradient-vip mb-4">
              More Than Gymnastics
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We're not just teaching flips and cartwheels. We're building confidence, 
              discipline, and lifelong skills that extend far beyond the gym.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="card-float glass group hover:scale-105 transition-transform duration-300"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Class Types Preview */}
      <section className="py-24 px-4 bg-gradient-to-b from-dark-900 to-dark-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge-vip mb-4">Our Programs</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gradient-vip mb-4">
              Find Your Perfect Class
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From recreational fun to competitive training, we have a program for every level and goal.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Recreational */}
            <div className="glass-gold rounded-3xl overflow-hidden group hover-lift">
              <div className="h-48 bg-gradient-to-br from-amber-500/30 to-orange-500/20 flex items-center justify-center">
                <span className="text-6xl">🎀</span>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gradient-gold mb-2">Recreational</h3>
                <p className="text-gray-400 mb-4">Fun, supportive classes for all ages and abilities. Perfect for beginners!</p>
                <Link to="/classes" className="text-amber-400 font-semibold hover:text-amber-300 transition-colors">
                  Explore Classes →
                </Link>
              </div>
            </div>

            {/* Development */}
            <div className="glass-pink rounded-3xl overflow-hidden group hover-lift">
              <div className="h-48 bg-gradient-to-br from-pink-500/30 to-rose-500/20 flex items-center justify-center">
                <span className="text-6xl">⭐</span>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gradient-pink mb-2">Development</h3>
                <p className="text-gray-400 mb-4">Building skills progressively. For members ready to advance their technique.</p>
                <Link to="/classes" className="text-pink-400 font-semibold hover:text-pink-300 transition-colors">
                  Explore Classes →
                </Link>
              </div>
            </div>

            {/* Competitive */}
            <div className="glass rounded-3xl overflow-hidden group hover-lift border-cyan-500/20">
              <div className="h-48 bg-gradient-to-br from-cyan-500/30 to-blue-500/20 flex items-center justify-center">
                <span className="text-6xl">🏅</span>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-cyan-400 mb-2">Competitive</h3>
                <p className="text-gray-400 mb-4">Elite training for aspiring champions. Competition-ready programs.</p>
                <Link to="/classes" className="text-cyan-400 font-semibold hover:text-cyan-300 transition-colors">
                  Explore Classes →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 relative overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge-vip mb-4">Testimonials</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gradient-vip mb-4">
              What Families Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="glass rounded-2xl p-6 hover-lift"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-amber-400">★</span>
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-amber-400 flex items-center justify-center text-white font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-white">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-pink-500/5 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-amber-500/5 blur-3xl" />
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="glass-strong rounded-3xl p-12 md:p-16 relative overflow-hidden">
            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-transparent to-amber-500/10" />
            
            <div className="relative z-10">
              <span className="badge-new mb-6">Start Today</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Begin Your Journey?
              </h2>
              <p className="text-gray-400 mb-8 max-w-xl mx-auto">
                Book your first class today and experience the Elite difference. 
                No commitment required - just come along and see for yourself!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/membership" className="btn-vip">
                  Book Free Trial
                </Link>
                <Link to="/contact" className="btn-ghost-vip">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Locations Preview */}
      <section className="py-16 px-4 bg-dark-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-2">Our Venues</h2>
            <p className="text-gray-400">Premium facilities across London</p>
          </div>
          
          <div className="glass rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold text-gradient-gold mb-2">Elite Gymnastics North</h3>
              <p className="text-gray-400">123 Training Way, Islington, N1 2ABC</p>
            </div>
            <Link 
              to="/locations" 
              className="px-6 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-white font-medium"
            >
              View All Locations
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}